/**
 * Helpers compartidos para los componentes de WhatsApp.
 */

import { differenceInSeconds, differenceInMinutes, differenceInHours, differenceInDays } from 'date-fns';
import { parseTokens } from '@/components/whatsapp/WaFormattedText';

/**
 * Hora compacta de un chat: "ahora", "3 min", "2 h", "ayer", "lun", "15/3".
 *
 * Vive acá y no en la lista de chats porque la usan tres pantallas (la bandeja, el buscador de
 * mensajes y el menú de chats del encabezado). Que dos listas muestren "2 h" y "hace 2 horas" para
 * el mismo mensaje es el tipo de detalle que hace sentir la app cosida a pedazos.
 */
export function waTimeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const secs = differenceInSeconds(now, date);
  if (secs < 60) return 'ahora';
  const mins = differenceInMinutes(now, date);
  if (mins < 60) return `${mins} min`;
  const hrs = differenceInHours(now, date);
  if (hrs < 24) return `${hrs} h`;
  const days = differenceInDays(now, date);
  if (days === 1) return 'ayer';
  if (days < 7) return date.toLocaleDateString('es', { weekday: 'short' });
  return date.toLocaleDateString('es', { day: 'numeric', month: 'numeric' });
}

/**
 * Tokens de tamaño de avatar — alineados con Polaris Avatar (`xs/sm/md/lg/xl`).
 * Úsalos siempre que rendices un avatar custom (`<span>` con foto/iniciales)
 * para mantener consistencia con `<Avatar size="..." />` de Polaris.
 *
 *   xs (20)  — inline en chats / mensajes
 *   sm (28)  — listas densas, color swatches
 *   md (36)  — fila de tabla estándar (DEFAULT)
 *   lg (40)  — cards, jerarquía visual
 *   xl (56)  — headers, detail pages
 *
 * NO inventes sizes intermedios. Si necesitas otro tamaño, el design system
 * está pidiendo otra cosa (icono, thumbnail, etc.), no un avatar.
 */
export const AVATAR_SIZE = {
  xs: 20,
  sm: 28,
  md: 36,
  lg: 40,
  xl: 56,
} as const;

export type AvatarSizeToken = keyof typeof AVATAR_SIZE;

/**
 * Alto de la cabecera del chat Y de los paneles que viven a su lado («Info. del contacto»). Es UN
 * número a propósito: medido el 12-oct-2026, la del chat medía 57 y la del panel 56, y las dos
 * líneas de abajo quedaban a un píxel una de la otra — eso se lee como desprolijo aunque nadie sepa
 * nombrar por qué. 40 del avatar + 8 arriba y 8 abajo + 1 del borde, con `border-box`.
 */
export const ALTO_DE_LA_CABECERA = 57;

export const AVATAR_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
];

/**
 * Paleta WhatsApp-themed para avatars/badges de líneas telefónicas.
 * Verdes/teals dominantes — refuerzan identidad WhatsApp en componentes
 * que representan canales (lista de conversaciones, tarjetas de líneas).
 *
 * NO usar para avatars de contactos genéricos — usa `AVATAR_COLORS` para eso.
 */
export const PHONE_LINE_COLORS = [
  '#25D366', '#128C7E', '#075E54', '#34B7F1',
  '#00A884', '#00838f', '#5c6bc0', '#7cb342',
];

/** Color determinístico basado en el nombre del contacto. */
export function getAvatarColor(name: string | null): string {
  const str = name ?? 'x';
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/**
 * Color de avatar para una línea WhatsApp — usa la paleta WhatsApp-themed.
 * Determinístico por nombre, igual que `getAvatarColor` pero con paleta verde.
 */
export function getPhoneLineAvatarColor(name: string | null): string {
  const str = name ?? 'x';
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  return PHONE_LINE_COLORS[Math.abs(hash) % PHONE_LINE_COLORS.length];
}

/** Iniciales del contacto: "LF" para "Luan Fuentes", últimos 2 dígitos del teléfono como fallback. */
/**
 * Las dos letras del avatar.
 *
 * 🚨 NO PUEDE TIRAR NUNCA, Y TIRABA. Terminaba en `fallbackPhone.slice(-2)` con
 * el teléfono declarado `string`. Desde ADR 0047 un contacto identificado por
 * LID no tiene teléfono, y muchos tampoco nombre — así que esto lanzaba
 * `TypeError` sobre `null`.
 *
 * Lo grave no fue el error, fue DÓNDE: esta función la usa el menú de chats del
 * ENCABEZADO, que se pinta en todas las pantallas. Un contacto sin nombre ni
 * número tumbaba el panel entero —incluido el Inicio— con un cartel que hablaba
 * del punto de venta. Nada en el mensaje apuntaba a un avatar.
 *
 * 🪤 Y EL COMPILADOR NO PODÍA AVISAR: este proyecto tiene `strictNullChecks`
 * apagado, así que declarar `string | null` no habría producido ni un error. Acá
 * la única red es que la función misma no confíe en lo que le pasan.
 */
export function getInitials(
  contactName: string | null | undefined,
  fallbackPhone: string | null | undefined,
  identificador?: string | null,
): string {
  const name = contactName?.trim();
  if (name) {
    const parts = name.split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0].substring(0, 2).toUpperCase();
  }

  // Sin nombre: los últimos dos dígitos de lo que haya. El identificador sirve
  // igual que el teléfono para que dos contactos no compartan avatar.
  const cola = (fallbackPhone ?? identificador ?? '').replace(/[^a-zA-Z0-9]/g, '').slice(-2);
  return cola || '?';
}

/**
 * Nombre a mostrar de una conversación/contacto. Fuente ÚNICA — NUNCA
 * expone identificadores crudos (UUID de conversación, LID de privacy
 * mode, g:GROUPID, JID). Orden de resolución:
 *   1. contact_name (si tiene)
 *   2. grupo sin nombre (contact_phone 'g:...') → "Grupo de WhatsApp"
 *   3. LID privacy mode (jid @lid, o teléfono con >13 dígitos = LID
 *      disfrazado) → "Contacto WhatsApp"
 *   4. teléfono real plausible (7-13 dígitos) → el teléfono tal cual
 *   5. cualquier otra cosa → "Contacto WhatsApp"
 * No recibe conversation_id: es imposible que devuelva un UUID.
 */
export function waDisplayName(
  contactName: string | null | undefined,
  contactPhone: string | null | undefined,
  contactJid?: string | null,
  /**
   * Clase del identificador del contacto (ADR 0022). Sin esto, una conversación
   * del canal web sin nombre caía en «Contacto WhatsApp» — una etiqueta que
   * miente sobre por dónde llegó el cliente.
   *
   * Opcional para no tocar los llamadores que ya existen: sin ella, se comporta
   * exactamente como antes.
   */
  contactKind?: string | null,
  /**
   * El @usuario de WhatsApp (Meta, 2026): un contacto puede no mostrar su número y llegar
   * identificado por BSUID (`contact_phone` = `bsuid:…`). Su @usuario es lo único legible que hay.
   */
  contactUsername?: string | null,
): string {
  const name = contactName?.trim();
  if (name) return name;

  const usuario = contactUsername?.trim();
  if (usuario) return usuario.startsWith('@') ? usuario : `@${usuario}`;

  if (contactKind && contactKind !== 'phone') {
    switch (contactKind) {
      case 'web':      return 'Visitante del sitio';
      case 'email':    return 'Contacto por correo';
      case 'external': return 'Contacto externo';
      default:         return 'Contacto';
    }
  }

  const phone = (contactPhone ?? '').trim();
  if (phone.startsWith('g:')) return 'Grupo de WhatsApp';
  // Sin número y sin @usuario (Meta sólo mandó el BSUID): no hay nada que mostrar que no sea inventado.
  if (phone.startsWith('bsuid:')) return 'Contacto WhatsApp';
  const isLid = (contactJid ?? '').endsWith('@lid');
  const digits = phone.replace(/\D/g, '');
  if (isLid || digits.length > 13) return 'Contacto WhatsApp';
  if (digits.length >= 7 && digits.length <= 13) return phone;
  return 'Contacto WhatsApp';
}

/**
 * Etiqueta corta de un mensaje para PREVIEWS (lista de chats, respuesta citada,
 * mensaje fijado, respuesta rápida).
 *
 * 🪤 Esto estaba reimplementado en 6 lugares con emojis divergentes: el mismo
 * audio se veía "🎵 Audio" en la lista y "🎤 Audio" en el thread. Fuente única.
 *
 * @param caption Texto del mensaje (body o media_caption). Si existe, gana:
 *                es lo que WhatsApp muestra ("📷 mi texto" en vez de "📷 Foto").
 */
/**
 * Saca los marcadores de formato para la VISTA PREVIA de una lista.
 *
 * 🚨 EL PROBLEMA QUE ESTO ARREGLA. En la burbuja del chat, `WaFormattedText` convierte `*Vitalex*` en
 * negrita. Pero la vista previa de la lista es texto plano —no hay lugar para estilos en un renglón
 * recortado— así que mostraba `*Vitalex*` con los asteriscos a la vista. El founder lo reportó sobre
 * los mensajes de calidad, que son los primeros que usan negrita: en su WhatsApp se veía Vitalex en
 * negrita y en nuestra lista con asteriscos.
 *
 * 🪤 SE QUITAN LOS MARCADORES, NO SE INTENTA ESTILAR. La vista previa vive en un `string` que se usa en
 * varios lados (la lista del módulo, el menú del encabezado, el título de una notificación); devolver
 * nodos de React obligaría a cambiarlos todos y a decidir qué hace cada uno con la negrita en un
 * renglón de 30 caracteres. Quitar el marcador conserva el texto y elimina el ruido.
 *
 * 🪤 SOLO SI ENVUELVE TEXTO REAL, la misma regla que usa WaFormattedText: `2*3*4` conserva sus
 * asteriscos porque son una multiplicación, no un formato.
 */
export function waSinMarcadoresDeFormato(texto: string): string {
  /*
   * SE REUSA `parseTokens`, EL MISMO QUE USA LA BURBUJA, y no un regex propio. Mi primera version
   * era una expresion escrita a mano que ademas salio mal -se perdio el backreference del cierre, asi
   * que no quitaba nada- pero el problema de fondo no era el error: era tener DOS definiciones de
   * "que cuenta como negrita". La burbuja y la vista previa habrian discrepado al primer ajuste de las
   * reglas, y el sintoma seria el que ya vimos: el mismo mensaje distinto en dos lugares de la misma
   * pantalla. Con el tokenizador compartido, la vista previa muestra exactamente el texto que la
   * burbuja pone en negrita, sin los marcadores.
   */
  return parseTokens(texto).map((t) => t.content).join('');
}

export function waMediaPreview(msgType: string | null | undefined, caption?: string | null): string {
  const text = caption?.trim() ? waSinMarcadoresDeFormato(caption.trim()) : caption?.trim();
  const icon = WA_PREVIEW_ICON[msgType ?? ''] ?? '';
  if (!icon) return text || '';
  return `${icon} ${text || WA_PREVIEW_LABEL[msgType ?? ''] || 'Archivo'}`;
}

const WA_PREVIEW_ICON: Record<string, string> = {
  image: '📷', video: '🎥', audio: '🎤', voice: '🎤',
  document: '📎', sticker: '🖼️', location: '📍', contact: '👤', order: '🛒',
};

const WA_PREVIEW_LABEL: Record<string, string> = {
  image: 'Foto', video: 'Video', audio: 'Audio', voice: 'Nota de voz',
  document: 'Documento', sticker: 'Sticker', location: 'Ubicación',
  contact: 'Contacto', order: 'Pedido',
};
